import {
  DataTypeStore,
  EntityDataByEntityFilterPayload,
  getEntityDataByEntityIdLayoutIdApiForFilter,
  getLayoutByIdApi,
  WorkflowInstanceTaskType,
} from '@app-store'
import {
  Accordion,
  CustomTable,
  NAMESPACE,
  SkeletonLoader,
  TableColumnType,
  columnTypesConst,
} from '@ui-kit'
import { STRINGS } from '../../utils/strings'
import { SetStateAction, useEffect, useRef, useState } from 'react'
import { ItemType } from '../../types/workflow.types'
import { ENTITY_FIELDS, ORDER_STATUS } from '../../utils/constants'
import { LayoutKey } from '../../types/pap.types'
import { FieldValues } from 'react-hook-form'

const CaptureShipmentRecordTask: React.FC<{
  recordId?: string
  task: WorkflowInstanceTaskType
}> = ({ recordId, task }) => {
  const [inflightData, setInflightData] = useState<DataTypeStore[]>([])
  const [inflightColumns, setInflightColumns] = useState<string[]>([])
  const [inflightTableColumns, setInflightTableColumns] = useState<
    TableColumnType<DataTypeStore>[]
  >([])
  const [loadingInflight, setLoadingInflight] = useState<boolean>(true)

  const [shipmentData, setShipmentData] = useState<DataTypeStore[]>([])
  const [shipmentColumns, setShipmentColumns] = useState<string[]>([])
  const [shipmentTableColumns, setShipmentTableColumns] = useState<
    TableColumnType<DataTypeStore>[]
  >([])
  const [loadingShipment, setLoadingShipment] = useState<boolean>(true)
  const layoutFunctionParamsRef = useRef<
    Partial<Record<LayoutKey, FieldValues>>
  >({})

  const fetchLayoutByKey = async (layoutKey: LayoutKey) => {
    try {
      const layoutInfo = task?.identifier?.layout_info?.[layoutKey]

      if (!layoutInfo) return

      const response = await getLayoutByIdApi({
        entityId: layoutInfo?.entity_id?.toString(),
        layoutId: layoutInfo?.layout_id?.toString(),
      })

      layoutFunctionParamsRef.current[layoutKey] =
        response?.data?.function_parameters

      const elements =
        response?.data?.layout_data?.pages?.[0]?.rows?.[0]?.columns?.[0]
          ?.elements ?? []

      const columns = elements?.map((item: ItemType) => item?.keyName)

      if (layoutKey === ENTITY_FIELDS?.CASE_PAP_DETAILS?.inflight) {
        setInflightColumns(columns)
      } else {
        setShipmentColumns(columns)
      }

      const tableColumns = elements?.map((item: ItemType) => {
        if (
          layoutKey === ENTITY_FIELDS?.CASE_PAP_DETAILS?.inflight &&
          item?.keyName === ENTITY_FIELDS?.CASE_PAP_DETAILS?.fulfillment_status
        ) {
          return {
            header: item?.meta?.label,
            accessor: item?.keyName,
            type: columnTypesConst.COMPONENT,
            renderComponent: (row: DataTypeStore) => {
              const status = row?.fulfillment_status
              const colorClass = getOrderStatusClass(status?.toString())
              return <span className={`${colorClass} font-bold`}>{status}</span>
            },
          }
        }

        return {
          header: item?.meta?.label ?? item?.keyName,
          accessor: item?.keyName,
        }
      })
      if (layoutKey === ENTITY_FIELDS?.CASE_PAP_DETAILS?.inflight) {
        setInflightTableColumns(tableColumns)
      } else {
        setShipmentTableColumns(tableColumns)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const buildPayload = (layoutKey: LayoutKey, selectCols: string[]) => {
    const layout = task?.identifier?.layout_info?.[layoutKey]

    return {
      entity_id: layout?.entity_id?.toString(),
      select: selectCols,
      page: 0,
      size: 25,
      related_entities: {},
      filters: [
        {
          criteria: [
            {
              direction: 'ASC',
              field_name: 'case_id',
              operation: 'EQUALS',
              values: [recordId ?? ''],
            },
            {
              direction: 'DESC',
              field_name: 'id',
            },
          ],
        },
      ],
      layout_id: layout?.layout_id ?? 0,
      entityName: layout?.entity_name ?? '',
      ...(layoutFunctionParamsRef.current[layoutKey]
        ? { function_parameters: layoutFunctionParamsRef.current[layoutKey] }
        : {}),
    }
  }

  const fetchData = async (
    layoutKey: LayoutKey,
    selectCols: string[],
    setData: (rows: SetStateAction<DataTypeStore[]>) => void,
    setLoading: (loading: boolean) => void
  ) => {
    try {
      const { entityName, ...payload } = buildPayload(layoutKey, selectCols)

      const responseData = await getEntityDataByEntityIdLayoutIdApiForFilter({
        payload: payload as EntityDataByEntityFilterPayload,
        entityName,
      })

      setData(responseData?.data?.content)
      setLoading(false)
      return responseData?.data
    } catch (err) {
      console.error(err)
    }
  }

  const fetchInflightData = () => {
    fetchData(
      ENTITY_FIELDS?.CASE_PAP_DETAILS?.inflight as LayoutKey,
      inflightColumns ?? [],
      setInflightData,
      setLoadingInflight
    )
  }
  const fetchShipmentData = () => {
    fetchData(
      ENTITY_FIELDS?.CASE_PAP_DETAILS?.shipment as LayoutKey,
      shipmentColumns ?? [],
      setShipmentData,
      setLoadingShipment
    )
  }
  const getOrderStatusClass = (status: string) => {
    if (!status) return ''

    switch (status?.trim()?.toUpperCase()) {
      case ORDER_STATUS.ORDER_NOT_STARTED:
        return 'text-orange-500 font-semibold'

      case ORDER_STATUS.PRESCRIPTION_RECEIVED:
        return 'text-cyan-500 font-semibold'

      case ORDER_STATUS.UNDER_REVIEW:
        return 'text-cyan-600 font-semibold'

      case ORDER_STATUS.READY_TO_SHIP:
        return 'text-cyan-600 font-semibold'

      case ORDER_STATUS.SHIPPED:
        return 'text-green-600 font-semibold'

      case ORDER_STATUS.CANCELLED:
        return 'text-red-600 font-semibold'

      default:
        return 'text-gray-500 font-medium'
    }
  }

  useEffect(() => {
    fetchLayoutByKey(ENTITY_FIELDS?.CASE_PAP_DETAILS?.inflight as LayoutKey)
    fetchLayoutByKey(ENTITY_FIELDS?.CASE_PAP_DETAILS?.shipment as LayoutKey)
  }, [])

  useEffect(() => {
    if (inflightColumns?.length) fetchInflightData()
  }, [inflightColumns])

  useEffect(() => {
    if (shipmentColumns?.length) fetchShipmentData()
  }, [shipmentColumns])

  useEffect(() => {
    if (inflightData?.length && inflightTableColumns?.length) {
      setLoadingInflight(false)
    }
  }, [inflightData])
  useEffect(() => {
    if (shipmentData?.length && shipmentTableColumns?.length) {
      setLoadingShipment(false)
    }
  }, [shipmentData])

  return (
    <>
      <div className="py-2 text-md font-bold">
        Current Order Status:{' '}
        <span
          className={`${getOrderStatusClass(
            !loadingInflight
              ? inflightData?.length > 0
                ? inflightData?.[0]?.fulfillment_status?.toString()
                : ORDER_STATUS.ORDER_NOT_STARTED
              : ''
          )} px-2`}
        >
          {!loadingInflight
            ? inflightData?.length > 0
              ? inflightData?.[0]?.fulfillment_status?.toString()?.toUpperCase()
              : ORDER_STATUS.ORDER_NOT_STARTED
            : 'N/A'}
        </span>
      </div>
      <Accordion heading={STRINGS.INFLIGHT_DETAILS} isDefaultActive>
        <div className={`h-full ${loadingInflight ? 'visible' : 'hidden'}`}>
          <SkeletonLoader columns={3} />
        </div>
        <CustomTable
          columns={inflightTableColumns}
          data={inflightData ?? []}
          emptyDataMessage={NAMESPACE.NO_RECORDS_FOUND}
        />
      </Accordion>
      <Accordion heading={STRINGS.SHIPMENT_DETAILS} isDefaultActive>
        <div className={`h-full ${loadingShipment ? 'visible' : 'hidden'}`}>
          <SkeletonLoader columns={3} />
        </div>
        <CustomTable
          columns={shipmentTableColumns}
          data={shipmentData ?? []}
          emptyDataMessage={NAMESPACE.NO_RECORDS_FOUND}
        />
      </Accordion>
    </>
  )
}

export default CaptureShipmentRecordTask
